import { fileDownload } from 'utils/fileDownload';
import { logError } from 'utils/sentryUtils';

import { mmFetch } from './mmFetch';
import { mmFetchWithResponse } from './mmFetchWithResponse';

const productionApi = {
    addBundleToCarousel: async (carouselID, sellableBundleID) => {
        const url = `api/marketplace/v1/carousels/${carouselID}/bundles/${sellableBundleID}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
        });
    },
    addItemToCarousel: async (carouselID, sellableItemID) => {
        const url = `api/marketplace/v1/carousels/${carouselID}/items/${sellableItemID}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
        });
    },
    removeBundleFromCarousel: async (carouselID, sellableBundleID) => {
        const url = `api/marketplace/v1/carousels/${carouselID}/bundles/${sellableBundleID}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    removeItemFromCarousel: async (carouselID, sellableItemID) => {
        const url = `api/marketplace/v1/carousels/${carouselID}/items/${sellableItemID}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    updateSellableItemCarousel: async (sendParams) => {
        const url = `api/marketplace/v1/carousels/items`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
    },
    deleteCarousel: async (carouselID) => {
        const url = `api/marketplace/v1/carousels/${carouselID}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    updateCarousel: async (carouselID, sendParams) => {
        const formData = new FormData();

        Object.keys(sendParams).forEach((key) => {
            formData.append(key, sendParams[key]);
        });

        const url = `api/marketplace/v1/carousels/${carouselID}`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    getCarousels: async (packGroupID, excludeV2 = true, filter = '') => {
        const url = `api/marketplace/v1/carousels?packGroupID=${packGroupID}&excludeV2=${excludeV2}&filter=${filter}`;
        return mmFetchWithResponse(url);
    },
    createCarousel: async (data) => {
        const formData = new FormData();

        Object.keys(data).forEach((key) => {
            formData.append(key, data[key]);
        });

        const url = `api/marketplace/v1/carousels`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    createUpdateCarouselV2: async (data) => {
        const response = await mmFetchWithResponse(`api/marketplace/v1/carousels/multiple`, {
            method: 'POST',
            body: JSON.stringify({
                packGroupID: data.packGroupID,
                carousels: data.carousels,
            }),
        });

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
    getPricing: async () => {
        const url = `api/customize/v1/pricing`;
        return mmFetchWithResponse(url);
    },
    downloadLineSlotMapCSV: async (packGroupID, dummyData = false) => {
        const filename = `packGroup-${packGroupID}-sellableItems.csv`;
        let url = `api/warehouse/v1/sellableItems/lsm/csv?packGroupID=${packGroupID}`;
        if (dummyData) {
            url += `&dummySlotted=true`;
        }
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            // Handle non-OK responses
            let errorMsg = 'Failed to download CSV';
            try {
                const errorData = await res.json();
                errorMsg = errorData.msg || errorData.message || errorMsg;
            } catch {
                errorMsg = `${errorMsg} (Status: ${res.status})`;
            }
            return { status: false, error: errorMsg };
        } catch (e) {
            logError(e);
            return { status: false, error: e.toString() };
        }
    },
    downloadFrozenCSV: async (packGroupID) => {
        const filename = `packGroup-${packGroupID}-frozen-sellableItems.csv`;
        const url = `api/warehouse/v1/sellableItems/lsm/frozen/csv?packGroupID=${packGroupID}`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
                return { status: true };
            }
            // Handle non-OK responses
            let errorMsg = 'Failed to download frozen CSV';
            try {
                const errorData = await res.json();
                errorMsg = errorData.msg || errorData.message || errorMsg;
            } catch {
                errorMsg = `${errorMsg} (Status: ${res.status})`;
            }
            return { status: false, error: errorMsg };
        } catch (e) {
            logError(e);
            return { status: false, error: e.toString() };
        }
    },
    downloadSellableItemTotalInventoryCSV: async (packGroupID) => {
        const filename = `packGroup-${packGroupID}-sellableItems.csv`;
        const url = `api/warehouse/v1/sellableItems/inventory/csv?packGroupID=${packGroupID}`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, filename);
            }
        } catch (e) {
            logError(e);
        }
    },
    getActiveLots: async () => {
        const url = `api/warehouse/v1/activeLots`;
        return mmFetchWithResponse(url);
    },
    getAutoPricingStatus: async (packGroupID) => {
        const url = `api/customize/v1/autoPrice/${packGroupID}`;
        return mmFetchWithResponse(url);
    },
    getPackGroups: async (queryParams) => {
        // {facilityID, week, year}
        let url = `api/warehouse/v1/packGroups`;

        if (queryParams && Object.keys(queryParams).length) {
            const queryString = Object.keys(queryParams)
                .filter((key) => !!queryParams[key])
                .map((key) => `${key}=${queryParams[key]}`)
                .join('&');
            url += `?${queryString}`;
        }

        return mmFetchWithResponse(url);
    },
    getPackGroupsRecent: async (facilityID, excludeFbm = 'false') => {
        let url = `api/warehouse/v1/packGroups/recent`;
        if (facilityID) {
            url += `?facilityID=${facilityID}&excludeFbm=${excludeFbm}`;
        }
        return mmFetchWithResponse(url);
    },
    getPackGroupsCurrentNext: async () => {
        const url = 'api/warehouse/v1/packGroups/currentNext';
        return mmFetchWithResponse(url);
    },
    getActivePackGroups: async (facilityID) => {
        let url = `api/warehouse/v1/packGroups?active=true`;
        if (facilityID) {
            url += `&facility=${facilityID}`;
        }
        return mmFetchWithResponse(url);
    },
    getPackGroupAssignments: async (week, year) => {
        let url = `api/warehouse/v1/packGroups/assignments`;
        if (week && year) {
            url += `?week=${week}&year=${year}`;
        }
        return mmFetchWithResponse(url);
    },
    getPlacedPOs: async () => {
        const url = `api/procurement/v1/purchaseOrder/items`;
        return mmFetchWithResponse(url);
    },
    getSellableBundles: async (packGroupID) => {
        const url = `api/inventory/v1/sellableBundles?packGroupID=${packGroupID}`;
        return mmFetchWithResponse(url);
    },
    getSellableItems: async (packGroupID) => {
        const url = `api/warehouse/v1/sellableItems?packGroupID=${packGroupID}`;
        return mmFetchWithResponse(url);
    },
    getSellableItemsTotalInventory: async (packGroupID) => {
        const url = `api/warehouse/v1/sellableItemInventory?packGroupID=${packGroupID}`;
        return mmFetchWithResponse(url);
    },
    getSellableItemMisfitsPlusDiscounts: async ({ week = '', year = '' }) => {
        let url = `api/membership/v1/item-discounts`;
        if (!!week && !!year) {
            url += `?week=${week}&year=${year}`;
        }
        return mmFetchWithResponse(url);
    },
    getSellableItemDiscountCSV: async ({ packGroupID, week, year }) => {
        const url = `api/membership/v1/item-discounts?week=${week}&year=${year}&packGroupID=${packGroupID}&csv=true`;
        try {
            const res = await mmFetch(url);
            if (res.ok === true) {
                await fileDownload(res, `item_discounts_week_${week}_year_${year}_packGroup_${packGroupID}.csv`);
            }
        } catch (e) {
            logError(e);
        }
    },
    updateMisfitsPlusDiscount: async (sellableItemID, plusMemberDiscount) => {
        const url = `api/membership/v1/item-discounts/${sellableItemID}`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ plusMemberDiscount }),
        });
    },
    uploadSellableItemMisfitsPlusDiscounts: async (file, packGroupID) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('packGroupID', packGroupID);

        const url = `api/membership/v1/item-discounts/upload`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    confirmSellableItemMisfitsPlusDiscounts: async (key) => {
        const url = 'api/membership/v1/item-discounts/confirm';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ key }),
        });
    },
    markPackGroupReadyForPricing: async (packGroupID) => {
        const url = 'api/customize/v1/autoPrice';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ packGroupID }),
        });
    },
    makePackGroup: async (item) => {
        const url = `api/warehouse/v1/packGroups`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(item),
        });
    },
    removeLotFromSellableItem: async (sellableItemID, lotID) => {
        const url = `api/warehouse/v1/sellableItems/${sellableItemID}/lots/${lotID}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    removePOFromSellableItem: async (sellableItemID, poMapID) => {
        const url = `api/warehouse/v1/sellableItems/${sellableItemID}/poMap/${poMapID}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    removeSellableItems: async (ids) => {
        const url = `api/warehouse/v1/sellableItems`;
        try {
            return mmFetchWithResponse(url, {
                method: 'DELETE',
                body: JSON.stringify({ sellableItems: ids }),
            });
        } catch (e) {
            logError(e);
            return { status: false, data: null };
        }
    },
    setPackGroupInventory: async (packGroupID, inventory) => {
        const url = `api/warehouse/v1/packGroups/${packGroupID}/inventory`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ inventory }),
        });
    },
    setLineSlotMap: async (key) => {
        const url = 'api/warehouse/v1/sellableItems/lsm/csv';
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ key }),
        });
    },
    setSellableItems: async (key) => {
        const url = 'api/warehouse/v1/sellableItems/bulk/csv';
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ key }),
        });
    },
    updatePackGroupAssignment: async ({ arrivalDay, facilityID, packGroupID }) => {
        const url = `api/warehouse/v1/packGroups/assignment`;
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ arrivalDay, facilityID, packGroupID }),
        });
    },
    updatePricing: async (id, field, value) => {
        const url = `api/customize/v1/pricing/${id}`;
        const sendParams = { [field]: value };
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
    },
    markItemSoldOut: async (id, reason, newValue) => {
        const url = 'api/warehouse/v1/sellableItems/soldOut';
        const sendParams = {
            sellableItemID: id,
            soldOut: newValue,
            reason,
        };
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
    },
    updateSellableItem: async (id, field, newValue) => {
        const url = `api/warehouse/v1/sellableItems/${id}`;
        const sendParams = { [field]: newValue };
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify(sendParams),
        });
    },
    uploadLineSlotMap: async (packGroupID, data) => {
        const formData = new FormData();
        formData.append('file', data.file);
        formData.append('packGroupID', packGroupID);

        const url = 'api/warehouse/v1/sellableItems/lsm/csv';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    uploadSellableItems: async (packGroupID, data) => {
        const formData = new FormData();
        formData.append('file', data.file);
        formData.append('packGroupID', packGroupID);

        const url = 'api/warehouse/v1/sellableItems/bulk/csv';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    uploadSellableItemTotalInventoryCSV: async ({ file, packGroupID }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('packGroupID', packGroupID);

        const url = 'api/warehouse/v1/sellableItems/inventory/csv';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    setSellableItemInventoryAmounts: async (key) => {
        const url = 'api/warehouse/v1/sellableItems/inventory/csv';
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ key }),
        });
    },
    confirmCarouselUpload: async (key) => {
        const url = 'api/production/v2/carousel/bulk';
        return mmFetchWithResponse(url, {
            method: 'PUT',
            body: JSON.stringify({ key }),
        });
    },
    uploadCarousels: async (data) => {
        const formData = new FormData();
        formData.append('itemFile', data.file);

        const url = 'api/production/v2/carousel/bulk';
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    getAisleBannerByTaxonomyID: async (taxonomyID) => {
        const url = `api/marketplace/v1/aisles/banner/${taxonomyID}`;
        return mmFetchWithResponse(url);
    },
    getAisleBanners: async () => {
        const url = `api/marketplace/v1/aisles/banners`;
        return mmFetchWithResponse(url);
    },
    getAisles: async () => {
        const url = `api/marketplace/v1/aisles`;
        return mmFetchWithResponse(url);
    },
    createAisleBanner: async (sendParams) => {
        const formData = new FormData();
        Object.keys(sendParams).forEach((key) => {
            formData.append(key, sendParams[key]);
        });

        return mmFetchWithResponse(`api/marketplace/v1/aisles/banner`, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    updateAisleBanner: async (sendParams) => {
        const { id } = sendParams;
        const formData = new FormData();
        Object.keys(sendParams).forEach((key) => {
            formData.append(key, sendParams[key]);
        });
        const url = `api/marketplace/v1/aisles/banner/${id}`;
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });
    },
    deleteAisleBanner: async (id) => {
        const url = `api/marketplace/v1/aisles/banner/${id}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    getCountriesOfOriginForSellableItems: async () => {
        const url = `api/warehouse/v1/sellableItems/countryOfOriginMap`;
        return mmFetchWithResponse(url);
    },
    createSellableItemCountryOfOriginMap: async (sellableItemID, field, values) => {
        const url = `api/warehouse/v1/sellableItems/${sellableItemID}/countryOfOriginMap`;
        const sendParams = { [field]: values };
        return mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify(sendParams),
        });
    },
    removeCountryOfOriginFromSellableItem: async (sellableItemID, countryID) => {
        const url = `api/warehouse/v1/sellableItems/${sellableItemID}/countryOfOriginMap/${countryID}`;
        return mmFetchWithResponse(url, {
            method: 'DELETE',
        });
    },
    downloadPrePopulatedCsv: async (sellableItemIds) => {
        const url = 'api/warehouse/v1/sellableItems/export/downloadPrePopulatedCsv';

        try {
            const res = await mmFetch(url, {
                method: 'POST',
                body: JSON.stringify(sellableItemIds),
            });
            if (res.ok === true) {
                await fileDownload(res, 'sellable-item-upload-template.csv');
            }
            return res;
        } catch (e) {
            logError(e);
            return undefined;
        }
    },
    getPackGroupDates: async (packGroupID) => {
        const url = `api/warehouse/v1/packGroups/${packGroupID}/packDates`;
        return mmFetchWithResponse(url);
    },
    getStorefrontConfigurationsByPackGroupID: async (packGroupID) => {
        const url = `api/marketplace/v1/storefrontConfigurations/packGroup/${packGroupID}`;
        const response = await mmFetchWithResponse(url);

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
    getStorefrontConfigurationByID: async (id) => {
        const url = `api/marketplace/v1/storefrontConfigurations/${id}`;
        const response = await mmFetchWithResponse(url);

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
    uploadStorefrontConfigImage: async (file) => {
        const url = 'api/marketplace/v1/uploadImage';

        const formData = new FormData();
        formData.append('imageFile', file);

        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: formData,
            processData: false,
            headers: {},
        });

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
    saveStorefrontConfiguration: async (data, update = false) => {
        const carouselTypes = ['PRODUCT_CAROUSEL_1', 'PRODUCT_CAROUSEL_2', 'PRODUCT_CAROUSEL_3', 'PRODUCT_SPOTLIGHT'];

        // Build a list of of slices that are powered by back-end carousels, maintaining their
        // index from the original storefront config.
        const carousels = data.slices.map((slice) =>
            carouselTypes.includes(slice.displayType)
                ? // Here, the `rankNumber` on the carousel comes from the `storefront_configuration_slices` table.
                  // However, the POST /multiple endpoint expects a `rankNumber` relative to the `carousels` table.
                  // Since carousel-level ranking isn't relevant for storefront configs, we explicitly set it to `null`.
                  {
                      ...slice,
                      rankNumber: null,
                  }
                : null,
        );

        let slicesWithCarouselIDs = data.slices;

        if (carousels.filter((carousel) => carousel !== null).length > 0) {
            // Create/update the carousels in the back-end.
            const carouselsResponse = await mmFetchWithResponse(`api/marketplace/v1/carousels/multiple`, {
                method: 'POST',
                body: JSON.stringify({
                    packGroupID: data.packGroupID,
                    carousels,
                }),
            });

            if (carouselsResponse.msg !== 'Success') {
                throw new Error(carouselsResponse.msg);
            }

            // A map of storefront config indices to their corresponding carousel IDs.
            // ex. { 0: '123', 3: '456', 4: '789' }
            const carouselsIDMap = carouselsResponse.data.created;

            // Attach the new carousel IDs to their corresponding slices.
            slicesWithCarouselIDs = data.slices.map((slice, index) => {
                if (carouselsIDMap[index]) {
                    return { ...slice, carouselID: carouselsIDMap[index] };
                }

                return slice;
            });
        }

        // Creating storefront config after all necessary carousels have been created.
        const storefrontConfigResponse = await mmFetchWithResponse(
            `api/marketplace/v1/storefrontConfigurations${update ? `/${data.id}` : ''}`,
            {
                method: update ? 'PUT' : 'POST',
                body: JSON.stringify({
                    ...data,
                    slices: slicesWithCarouselIDs,
                }),
            },
        );

        if (storefrontConfigResponse.msg !== 'Success') {
            throw new Error(storefrontConfigResponse.msg);
        }

        return storefrontConfigResponse;
    },
    deleteStorefrontConfiguration: async (id) => {
        const url = `api/marketplace/v1/storefrontConfigurations/${id}`;
        const response = await mmFetchWithResponse(url, {
            method: 'DELETE',
        });

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
    getSellableItemsByExternalSku: async (carousels, packGroupID) => {
        const url = 'api/marketplace/v1/storefrontConfigurations/sellable-items/resolve-by-external-sku';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ carousels, packGroupID }),
        });

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
    getConvertedSellableItems: async (packGroupID, carousels) => {
        const url = 'api/marketplace/v1/storefrontConfigurations/sellable-items/resolve-by-id';
        const response = await mmFetchWithResponse(url, {
            method: 'POST',
            body: JSON.stringify({ carousels, packGroupID }),
        });

        if (response.msg !== 'Success') {
            throw new Error(response.msg);
        }

        return response;
    },
};

export { productionApi };
